import React, { useCallback, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FiCheck, FiX, FiCamera } from "react-icons/fi";
import { photoService } from "../services/api";
import pLimit from "p-limit";

interface PhotoUploadProps {
  onUploadComplete?: (allSuccessful: boolean) => void;
}

interface UploadState {
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  error?: string;
  s3Uploaded?: boolean;
  uploadUrl?: string;
  fields?: Record<string, string>;
  key?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ onUploadComplete }) => {
  const [uploadProgress, setUploadProgress] = useState<UploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploaderName, setUploaderName] = useState("");

  const isUploadingRef = useRef(false);
  const uploadedFilesRef = useRef<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);

  const limit = pLimit(10);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (isUploadingRef.current) {
        return;
      }

      if (limit.pendingCount > 0) {
        return;
      }

      if (!uploaderName.trim()) {
        return;
      }

      const newFiles = acceptedFiles.filter((file) => {
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        if (uploadedFilesRef.current.has(fileKey)) {
          return false;
        }
        uploadedFilesRef.current.add(fileKey);
        return true;
      });

      if (newFiles.length === 0) {
        return;
      }

      isUploadingRef.current = true;
      setIsUploading(true);
      setTotalFiles((prev) => prev + newFiles.length);
      setOverallProgress(0);

      abortControllerRef.current = new AbortController();

      const newProgress: UploadState[] = newFiles.map((file) => ({
        file,
        progress: 0,
        status: "uploading",
      }));

      setUploadProgress((prev) => [...prev, ...newProgress]);

      const uploadPromises = newFiles.map(async (file) => {
        return limit(async () => {
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(
              () => reject(new Error(`Upload timeout for ${file.name}`)),
              30000
            );
          });

          const uploadPromise = (async () => {
            try {
              setUploadProgress((prev) =>
                prev.map((item) =>
                  item.file === file
                    ? { ...item, progress: 25, status: "uploading" }
                    : item
                )
              );

              const { upload_url, fields, key } =
                await photoService.getUploadUrl(
                  file,
                  uploaderName.trim(),
                  abortControllerRef.current?.signal
                );

              setUploadProgress((prev) =>
                prev.map((item) =>
                  item.file === file
                    ? {
                        ...item,
                        progress: 50,
                        status: "uploading",
                        uploadUrl: upload_url,
                        fields: fields,
                        key: key,
                      }
                    : item
                )
              );

              await photoService.uploadToS3(
                file,
                upload_url,
                fields,
                abortControllerRef.current?.signal
              );

              setUploadProgress((prev) =>
                prev.map((item) =>
                  item.file === file
                    ? {
                        ...item,
                        progress: 75,
                        status: "uploading",
                        s3Uploaded: true,
                      }
                    : item
                )
              );

              await photoService.completeUpload(
                key,
                uploaderName.trim(),
                abortControllerRef.current?.signal
              );

              setUploadProgress((prev) =>
                prev.map((item) =>
                  item.file === file
                    ? {
                        ...item,
                        progress: 100,
                        status: "success",
                      }
                    : item
                )
              );

              setUploadedCount((prev) => {
                const newCount = prev + 1;
                const totalFiles = uploadProgress.length + newFiles.length;
                const progress = Math.min((newCount / totalFiles) * 100, 100);
                setOverallProgress(progress);
                return newCount;
              });

              return { success: true, file };
            } catch (error) {
              console.error(`Upload failed for ${file.name}:`, error);
              setUploadProgress((prev) =>
                prev.map((item) =>
                  item.file === file
                    ? {
                        ...item,
                        progress: 0,
                        status: "error",
                        error:
                          error instanceof Error
                            ? error.message
                            : "Upload failed",
                      }
                    : item
                )
              );
              return { success: false, file, error };
            }
          })();

          return Promise.race([uploadPromise, timeoutPromise]);
        });
      });

      const results = await Promise.all(uploadPromises);

      const failedUploads = results.filter((result) => !result.success);

      setOverallProgress(100);

      const allSuccessful = failedUploads.length === 0;
      if (onUploadComplete) {
        onUploadComplete(allSuccessful);
      }

      setIsUploading(false);
      isUploadingRef.current = false;
      setTimeout(() => {
        uploadedFilesRef.current.clear();
      }, 1000);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    },
    [onUploadComplete, uploadProgress.length, uploaderName]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".bmp", ".webp"],
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024,
    disabled: isUploading || !uploaderName.trim(),
  });

  const clearProgress = () => {
    setUploadProgress([]);
    setOverallProgress(0);
    setUploadedCount(0);
    setTotalFiles(0);
    uploadedFilesRef.current.clear();
  };

  const retryFailedUploads = async () => {
    const failedItems = uploadProgress.filter(
      (item) => item.status === "error"
    );

    if (failedItems.length === 0) return;

    setIsUploading(true);
    setOverallProgress(0);

    abortControllerRef.current = new AbortController();

    const retryPromises = failedItems.map(async (item) => {
      try {
        if (item.s3Uploaded && item.key) {
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.file === item.file
                ? { ...p, progress: 75, status: "uploading" }
                : p
            )
          );

          await photoService.completeUpload(
            item.key,
            uploaderName.trim(),
            abortControllerRef.current?.signal
          );

          setUploadProgress((prev) =>
            prev.map((p) =>
              p.file === item.file
                ? { ...p, progress: 100, status: "success" }
                : p
            )
          );

          setUploadedCount((prev) => {
            const newCount = prev + 1;
            const progress = Math.min(
              (newCount / uploadProgress.length) * 100,
              100
            );
            setOverallProgress(progress);
            return newCount;
          });

          return { success: true, file: item.file };
        } else {
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.file === item.file
                ? { ...p, progress: 0, status: "uploading" }
                : p
            )
          );

          return limit(async () => {
            try {
              const { upload_url, fields, key } =
                await photoService.getUploadUrl(
                  item.file,
                  uploaderName.trim(),
                  abortControllerRef.current?.signal
                );

              setUploadProgress((prev) =>
                prev.map((p) =>
                  p.file === item.file
                    ? {
                        ...p,
                        progress: 25,
                        status: "uploading",
                        uploadUrl: upload_url,
                        fields: fields,
                        key: key,
                      }
                    : p
                )
              );

              await photoService.uploadToS3(
                item.file,
                upload_url,
                fields,
                abortControllerRef.current?.signal
              );

              setUploadProgress((prev) =>
                prev.map((p) =>
                  p.file === item.file
                    ? {
                        ...p,
                        progress: 75,
                        status: "uploading",
                        s3Uploaded: true,
                      }
                    : p
                )
              );

              await photoService.completeUpload(
                key,
                uploaderName.trim(),
                abortControllerRef.current?.signal
              );

              setUploadProgress((prev) =>
                prev.map((p) =>
                  p.file === item.file
                    ? { ...p, progress: 100, status: "success" }
                    : p
                )
              );

              setUploadedCount((prev) => {
                const newCount = prev + 1;
                const progress = Math.min(
                  (newCount / uploadProgress.length) * 100,
                  100
                );
                setOverallProgress(progress);
                return newCount;
              });

              return { success: true, file: item.file };
            } catch (error) {
              console.error(
                `Retry upload failed for ${item.file.name}:`,
                error
              );
              setUploadProgress((prev) =>
                prev.map((p) =>
                  p.file === item.file
                    ? {
                        ...p,
                        progress: 0,
                        status: "error",
                        error:
                          error instanceof Error
                            ? error.message
                            : "Upload failed",
                      }
                    : p
                )
              );
              return { success: false, file: item.file, error };
            }
          });
        }
      } catch (error) {
        console.error(`Retry upload failed for ${item.file.name}:`, error);
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.file === item.file
              ? {
                  ...p,
                  progress: 0,
                  status: "error",
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : p
          )
        );
        return { success: false, file: item.file, error };
      }
    });

    const results = await Promise.all(retryPromises);
    const allSuccessful = results.every(
      (result: { success: boolean; file: File }) => result.success
    );

    if (onUploadComplete) {
      onUploadComplete(allSuccessful);
    }

    setIsUploading(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsUploading(false);
      isUploadingRef.current = false;
      setUploadProgress((prev) =>
        prev.map((item) =>
          item.status === "uploading"
            ? {
                ...item,
                progress: 0,
                status: "error",
                error: "Upload cancelled",
              }
            : item
        )
      );
    }
  };

  const successCount = uploadProgress.filter(
    (item) => item.status === "success"
  ).length;
  const errorCount = uploadProgress.filter(
    (item) => item.status === "error"
  ).length;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <input
          type="text"
          value={uploaderName}
          onChange={(e) => setUploaderName(e.target.value)}
          placeholder="Digite seu nome completo"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-olive-green focus:border-transparent transition-all duration-200"
          disabled={isUploading}
        />
      </div>

      <div
        {...getRootProps()}
        className={`upload-zone ${isDragActive ? "active" : ""} ${
          isUploading ? "pointer-events-none opacity-50" : ""
        } ${!uploaderName.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-olive-cream rounded-full flex items-center justify-center">
              <FiCamera className="w-8 h-8 text-olive-green" />
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg font-semibold text-gray-900">
              {isDragActive ? "Solte as fotos aqui" : "Selecionar Fotos"}
            </h4>
            <p className="text-sm text-gray-600">
              {!uploaderName.trim()
                ? "Digite seu nome acima para começar a enviar"
                : "Escolha da sua galeria ou tire novas fotos"}
            </p>
          </div>
        </div>
      </div>

      {isUploading && (
        <div className="status-card animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Enviando...</h4>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600">
                {uploadedCount}/{totalFiles}
              </span>
              <button
                onClick={cancelUpload}
                className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                title="Cancelar envio"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progresso</span>
                <span className="font-semibold">
                  {overallProgress > 0 ? Math.round(overallProgress) : 0}%
                </span>
              </div>
              <div className="progress-bar">
                {overallProgress === 0 ? (
                  <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer rounded-full"></div>
                ) : (
                  <div
                    className="progress-fill"
                    style={{ width: `${overallProgress}%` }}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                {successCount > 0 && (
                  <div className="flex items-center space-x-1 text-green-600">
                    <FiCheck className="w-4 h-4" />
                    <span>{successCount}</span>
                  </div>
                )}
                {errorCount > 0 && (
                  <div className="flex items-center space-x-1 text-red-600">
                    <FiX className="w-4 h-4" />
                    <span>{errorCount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isUploading && uploadProgress.length > 0 && (
        <div className="status-card animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Envio Concluído
            </h4>
            <button
              onClick={clearProgress}
              className="text-gray-400 hover:text-gray-600 p-2"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-center space-x-6 text-sm">
              {successCount > 0 && (
                <div className="flex items-center space-x-2 text-green-600">
                  <FiCheck className="w-5 h-5" />
                  <span className="font-medium">{successCount} enviadas</span>
                </div>
              )}
              {errorCount > 0 && (
                <div className="flex items-center space-x-2 text-red-600">
                  <FiX className="w-5 h-5" />
                  <span className="font-medium">{errorCount} falharam</span>
                </div>
              )}
            </div>

            {errorCount > 0 && (
              <div className="space-y-3">
                <div className="text-center text-red-600 bg-red-50 rounded-2xl p-3 text-sm">
                  Alguns envios falharam. Você pode tentar novamente.
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={retryFailedUploads}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-medium transition-colors duration-200"
                  >
                    Tentar Novamente ({errorCount})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
